import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Sparkles, Trash2, Pencil, ArrowLeft, Eye, Save, Home, ClipboardList, Newspaper, Trophy } from "lucide-react";
import { API, axios, getToken, setToken, clearToken, authHeaders, apiErr } from "@/lib/api";
import AdminOrders from "@/components/AdminOrders";
import AdminReferrals from "@/components/AdminReferrals";
import { useSEO } from "@/lib/seo";

const empty = {
  id: null, title: "", excerpt: "", meta_description: "", keywords: "",
  cover_image: "", category: "Guides", content: "", status: "draft",
};

const categories = ["Guides", "SEO", "Branding", "Getting Started", "Marketing"];

const Admin = () => {
  const navigate = useNavigate();
  useSEO({ title: "Admin | BrandToBuilt", path: "/admin", noindex: true });
  const [authed, setAuthed] = useState(null);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("orders");
  const [view, setView] = useState("list");
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(empty);
  const [gen, setGen] = useState({ topic: "", keyword: "" });
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    axios.get(`${API}/auth/me`, authHeaders())
      .then(() => { setAuthed(true); loadPosts(); })
      .catch(() => { clearToken(); setAuthed(false); });
  }, []);

  const loadPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/posts`, authHeaders());
      setPosts(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds);
      setToken(data.token);
      setAuthed(true);
      loadPosts();
      toast.success(`Welcome back, ${data.name}`);
    } catch (e) { toast.error(apiErr(e, "Wrong email or password")); }
    finally { setLoggingIn(false); }
  };

  const logout = () => { clearToken(); setAuthed(false); setCreds({ email: "", password: "" }); };

  const openNew = () => { setForm(empty); setGen({ topic: "", keyword: "" }); setView("editor"); };

  const openEdit = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/posts/${id}`, authHeaders());
      setForm({ ...data, keywords: (data.keywords || []).join(", ") });
      setView("editor");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const generate = async () => {
    if (gen.topic.trim().length < 3) { toast.error("Enter a topic first."); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/generate`, gen, authHeaders());
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        excerpt: data.excerpt || "",
        meta_description: data.meta_description || "",
        keywords: (data.keywords || []).join(", "),
        category: data.category || f.category,
        content: data.content || "",
        cover_image: data.cover_image || f.cover_image,
      }));
      toast.success("Draft written! Review and publish when ready.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setGenerating(false); }
  };

  const save = async (publish) => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content are required."); return; }
    setSaving(true);
    const payload = {
      title: form.title, excerpt: form.excerpt, meta_description: form.meta_description,
      keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      cover_image: form.cover_image, category: form.category,
      content: form.content, status: publish ? "published" : form.status,
    };
    try {
      if (form.id) await axios.put(`${API}/admin/posts/${form.id}`, payload, authHeaders());
      else await axios.post(`${API}/admin/posts`, payload, authHeaders());
      toast.success(publish ? "Published!" : "Saved.");
      await loadPosts();
      setView("list");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    try {
      await axios.delete(`${API}/admin/posts/${id}`, authHeaders());
      toast.success("Deleted.");
      loadPosts();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // ---------- Login screen ----------
  if (authed === null) return <div className="min-h-screen flex justify-center items-center text-zinc-500"><Loader2 className="animate-spin" /></div>;

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-md bg-[#121212] border border-white/10 rounded-2xl p-8">
          <p className="font-display font-black text-2xl mb-1">brand<span className="text-[#ccff00]">to</span>built</p>
          <p className="text-zinc-500 mb-8 text-sm">Admin — blog dashboard</p>
          <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Email</label>
          <input type="email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 mb-4 text-white focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none" data-testid="admin-email" />
          <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Password</label>
          <input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 mb-6 text-white focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none" data-testid="admin-password" />
          <button type="submit" disabled={loggingIn} className="w-full rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-sm py-3.5 hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2" data-testid="admin-login-btn">
            {loggingIn ? <Loader2 size={18} className="animate-spin" /> : "Log in"}
          </button>
          <button type="button" onClick={() => navigate("/")} className="w-full text-center text-zinc-500 text-sm mt-4 hover:text-white transition-colors">← Back to site</button>
        </form>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 sticky top-0 bg-black/80 backdrop-blur-xl z-10">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <p className="font-display font-black text-lg">brand<span className="text-[#ccff00]">to</span>built <span className="text-zinc-500 font-normal text-sm">/ admin</span></p>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1.5 px-3 py-2" data-testid="admin-home"><Home size={15} /> Site</button>
            <button onClick={logout} className="text-sm text-zinc-400 hover:text-[#ff0055] flex items-center gap-1.5 px-3 py-2" data-testid="admin-logout"><LogOut size={15} /> Log out</button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-12 flex items-center gap-2">
          <button onClick={() => setTab("orders")} className={`inline-flex items-center gap-1.5 rounded-full text-xs font-bold uppercase tracking-widest px-4 py-2 transition-colors ${tab === "orders" ? "bg-[#ccff00] text-black" : "text-zinc-400 hover:text-white"}`} data-testid="admin-tab-orders"><ClipboardList size={14} /> Orders</button>
          <button onClick={() => setTab("referrals")} className={`inline-flex items-center gap-1.5 rounded-full text-xs font-bold uppercase tracking-widest px-4 py-2 transition-colors ${tab === "referrals" ? "bg-[#ccff00] text-black" : "text-zinc-400 hover:text-white"}`} data-testid="admin-tab-referrals"><Trophy size={14} /> Referrals</button>
          <button onClick={() => setTab("blog")} className={`inline-flex items-center gap-1.5 rounded-full text-xs font-bold uppercase tracking-widest px-4 py-2 transition-colors ${tab === "blog" ? "bg-[#ccff00] text-black" : "text-zinc-400 hover:text-white"}`} data-testid="admin-tab-blog"><Newspaper size={14} /> Blog</button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        {tab === "orders" ? (
          <AdminOrders />
        ) : tab === "referrals" ? (
          <AdminReferrals />
        ) : view === "list" ? (
          <>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display font-black text-3xl">Articles <span className="text-zinc-600 text-xl">({posts.length})</span></h1>
              <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:scale-105 transition-transform" data-testid="admin-new-post"><Plus size={16} /> New article</button>
            </div>
            <div className="space-y-3">
              {posts.map((p) => (
                <div key={p.id} className="flex items-center gap-4 bg-[#121212] border border-white/10 rounded-xl p-4" data-testid={`admin-post-${p.slug}`}>
                  {p.cover_image && <img src={p.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{p.title}</p>
                    <div className="flex items-center gap-2 text-xs mt-1">
                      <span className={`rounded-full px-2 py-0.5 ${p.status === "published" ? "bg-[#ccff00]/20 text-[#ccff00]" : "bg-white/10 text-zinc-400"}`}>{p.status}</span>
                      <span className="text-zinc-500">{p.category}</span>
                    </div>
                  </div>
                  <button onClick={() => openEdit(p.id)} className="p-2 text-zinc-400 hover:text-[#ccff00]" title="Edit" data-testid={`edit-${p.slug}`}><Pencil size={17} /></button>
                  {p.status === "published" && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 text-zinc-400 hover:text-white" title="View"><Eye size={17} /></a>}
                  <button onClick={() => del(p.id)} className="p-2 text-zinc-400 hover:text-[#ff0055]" title="Delete" data-testid={`delete-${p.slug}`}><Trash2 size={17} /></button>
                </div>
              ))}
              {posts.length === 0 && <p className="text-zinc-500 py-10 text-center">No articles yet. Create your first with AI.</p>}
            </div>
          </>
        ) : (
          <>
            <button onClick={() => setView("list")} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6" data-testid="editor-back"><ArrowLeft size={16} /> Back to articles</button>

            <div className="bg-[#0f0f0f] border border-[#ccff00]/30 rounded-2xl p-6 mb-8">
              <p className="flex items-center gap-2 font-display font-bold text-lg mb-4"><Sparkles size={18} className="text-[#ccff00]" /> Write with AI</p>
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <input value={gen.topic} onChange={(e) => setGen({ ...gen, topic: e.target.value })} placeholder="Topic — e.g. Why plumbers need a website" className="bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none" data-testid="gen-topic" />
                <input value={gen.keyword} onChange={(e) => setGen({ ...gen, keyword: e.target.value })} placeholder="Target keyword (optional)" className="bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none" data-testid="gen-keyword" />
              </div>
              <button onClick={generate} disabled={generating} className="inline-flex items-center gap-2 rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:scale-105 transition-transform disabled:opacity-60" data-testid="gen-submit">
                {generating ? <><Loader2 size={16} className="animate-spin" /> Writing…</> : <><Sparkles size={16} /> Draft article</>}
              </button>
            </div>

            <div className="space-y-4">
              <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="f-title" />
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none" data-testid="f-category">
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <Field label="Cover image URL" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testid="f-cover" />
              </div>
              <Field label="Excerpt (short hook)" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} testid="f-excerpt" />
              <Field label="Meta description (SEO)" value={form.meta_description} onChange={(v) => setForm({ ...form, meta_description: v })} testid="f-meta" />
              <Field label="Keywords (comma separated)" value={form.keywords} onChange={(v) => setForm({ ...form, keywords: v })} testid="f-keywords" />
              <div>
                <Label>Content (HTML)</Label>
                <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={16} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-sm placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none resize-y" data-testid="f-content" />
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <button onClick={() => save(false)} disabled={saving} className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white font-bold uppercase tracking-widest text-xs px-6 py-3.5 hover:bg-white/5 transition-colors disabled:opacity-60" data-testid="save-draft"><Save size={15} /> Save draft</button>
                <button onClick={() => save(true)} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-xs px-6 py-3.5 hover:scale-105 transition-transform disabled:opacity-60" data-testid="save-publish">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Publish
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Label = ({ children }) => <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">{children}</label>;
const Field = ({ label, value, onChange, testid }) => (
  <div>
    <Label>{label}</Label>
    <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:ring-2 focus:ring-[#ccff00] focus:border-transparent outline-none" data-testid={testid} />
  </div>
);

export default Admin;
